import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react'
import { api } from '@/services/api'

// Service health status types
type ServiceHealth = 'healthy' | 'degraded' | 'down' | 'unknown'

interface ServiceInfo {
  status: ServiceHealth
  message?: string
  provider: string
  latencyMs?: number
  errorRate?: number
}

interface ServiceStatusState {
  overall: ServiceHealth
  services: {
    database: ServiceInfo
    ai: ServiceInfo
  }
  affectedProvider: string | null
  detectedAt: Date | null
  userDismissed: boolean
  isCollapsed: boolean
  consecutiveFailures: number
}

interface ServiceStatusContextType extends ServiceStatusState {
  dismissBanner: () => void
  collapseBanner: () => void
  expandBanner: () => void
  reportError: (service: 'database' | 'ai', error: Error) => void
}

const ServiceStatusContext = createContext<ServiceStatusContextType | undefined>(undefined)

// Polling intervals
const NORMAL_POLL_INTERVAL = 60000 // 60 seconds when healthy
const INCIDENT_POLL_INTERVAL = 15000 // 15 seconds during incidents
const MIN_FAILURES_TO_SHOW = 2 // Require 2+ consecutive failures before showing degraded

// Session storage key for dismissal state
const DISMISSED_KEY = 'service_status_dismissed_at'
const COLLAPSED_KEY = 'service_status_collapsed'

// Provider status page URLs
export const STATUS_PAGE_URLS: Record<string, string> = {
  supabase: 'https://status.supabase.com',
  openai: 'https://status.openai.com',
  anthropic: 'https://status.anthropic.com',
  clerk: 'https://status.clerk.com',
  cloudflare: 'https://www.cloudflarestatus.com'
}

// Messages for different service states
export const SERVICE_MESSAGES: Record<string, Record<ServiceHealth, { headline: string; subtext: string }>> = {
  database: {
    healthy: { headline: '', subtext: '' },
    degraded: {
      headline: "Hang tight! Things are a bit slow right now.",
      subtext: "Our database is taking a coffee break. Your data is safe and sound - we're on it!"
    },
    down: {
      headline: "We hit a speed bump.",
      subtext: "Our database provider is having a moment. Don't worry - your data is completely safe. We're keeping an eye on things."
    },
    unknown: { headline: '', subtext: '' }
  },
  ai: {
    healthy: { headline: '', subtext: '' },
    degraded: {
      headline: "AI is thinking extra hard today.",
      subtext: "Our AI features are running slower than usual. Grab a snack - we'll be back to lightning speed soon!"
    },
    down: {
      headline: "Our AI is taking a quick nap.",
      subtext: "AI features are temporarily unavailable. The basics still work great while we sort this out."
    },
    unknown: { headline: '', subtext: '' }
  },
  multiple: {
    healthy: { headline: '', subtext: '' },
    degraded: {
      headline: "A few bumps in the road.",
      subtext: "Some features are taking a breather. Your data is safe - we're working to smooth things out!"
    },
    down: {
      headline: "A few bumps in the road.",
      subtext: "Some features are taking a breather. Your data is safe - we're working to smooth things out!"
    },
    unknown: { headline: '', subtext: '' }
  }
}

// Initial state
const initialState: ServiceStatusState = {
  overall: 'healthy',
  services: {
    database: { status: 'healthy', provider: 'supabase' },
    ai: { status: 'healthy', provider: 'openai' }
  },
  affectedProvider: null,
  detectedAt: null,
  userDismissed: false,
  isCollapsed: false,
  consecutiveFailures: 0
}

export function ServiceStatusProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ServiceStatusState>(() => {
    // Restore collapsed state from session storage (with SSR safety)
    try {
      const collapsed = typeof window !== 'undefined' && sessionStorage.getItem(COLLAPSED_KEY) === 'true'
      return { ...initialState, isCollapsed: collapsed }
    } catch {
      return initialState
    }
  })

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const visibilityRef = useRef(true)
  const previousOverallRef = useRef<ServiceHealth>('healthy')

  // Fetch health status from API
  const checkHealth = useCallback(async () => {
    // Don't poll if tab is hidden
    if (!visibilityRef.current) return

    try {
      const response = await api.get('/health/status')
      const data = response.data

      // Check if we should dispatch recovery event (before setState to avoid side effects in callback)
      const shouldDispatchRecovery =
        previousOverallRef.current !== 'healthy' &&
        data.overall === 'healthy'

      setState(prev => {
        const wasHealthy = prev.overall === 'healthy'
        const isNowHealthy = data.overall === 'healthy'

        // Check if user previously dismissed this incident (with SSR safety)
        let userDismissed = false
        try {
          const dismissedAt = typeof window !== 'undefined' ? sessionStorage.getItem(DISMISSED_KEY) : null
          userDismissed = dismissedAt !== null && prev.detectedAt !== null &&
            new Date(dismissedAt).getTime() >= prev.detectedAt.getTime()
        } catch {
          // sessionStorage not available
        }

        // Only mark as newly detected if transitioning from healthy to unhealthy
        const detectedAt = wasHealthy && !isNowHealthy
          ? new Date()
          : (isNowHealthy ? null : prev.detectedAt)

        return {
          overall: data.overall,
          services: {
            database: data.services?.database || prev.services.database,
            ai: data.services?.ai || prev.services.ai
          },
          affectedProvider: data.affectedProvider || null,
          detectedAt,
          userDismissed: isNowHealthy ? false : userDismissed,
          isCollapsed: prev.isCollapsed,
          consecutiveFailures: isNowHealthy ? 0 : prev.consecutiveFailures
        }
      })

      // Dispatch recovery event outside of setState (side effects should not be in setState)
      if (shouldDispatchRecovery) {
        window.dispatchEvent(new CustomEvent('service-status-recovered'))
      }

      previousOverallRef.current = data.overall
    } catch (error) {
      // Health check itself failed - increment failure counter
      setState(prev => ({
        ...prev,
        consecutiveFailures: prev.consecutiveFailures + 1,
        // Only show degraded after multiple consecutive failures
        overall: prev.consecutiveFailures >= MIN_FAILURES_TO_SHOW - 1 ? 'degraded' : prev.overall,
        detectedAt: prev.consecutiveFailures >= MIN_FAILURES_TO_SHOW - 1 && !prev.detectedAt
          ? new Date()
          : prev.detectedAt
      }))
    }
  }, [])

  // Setup polling with adaptive interval
  useEffect(() => {
    const startPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }

      // Use shorter interval during incidents
      const interval = state.overall !== 'healthy'
        ? INCIDENT_POLL_INTERVAL
        : NORMAL_POLL_INTERVAL

      pollIntervalRef.current = setInterval(checkHealth, interval)
    }

    // Initial check
    checkHealth()
    startPolling()

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [checkHealth, state.overall])

  // Handle visibility changes to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      visibilityRef.current = !document.hidden
      if (!document.hidden) {
        // Immediately check health when tab becomes visible again
        checkHealth()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [checkHealth])

  // Listen for passive error detection events from axios interceptor
  useEffect(() => {
    const handleServiceError = (event: CustomEvent<{ service: 'database' | 'ai'; status: number; url: string; message: string }>) => {
      const { service, message } = event.detail
      console.warn('⚠️ [ServiceStatus] Received service error event:', event.detail)

      setState(prev => {
        const newConsecutiveFailures = prev.consecutiveFailures + 1

        // Only show degraded after multiple failures
        if (newConsecutiveFailures < MIN_FAILURES_TO_SHOW) {
          return { ...prev, consecutiveFailures: newConsecutiveFailures }
        }

        return {
          ...prev,
          overall: 'degraded',
          services: {
            ...prev.services,
            [service]: {
              ...prev.services[service],
              status: 'degraded',
              message
            }
          },
          affectedProvider: prev.services[service].provider,
          detectedAt: prev.detectedAt || new Date(),
          consecutiveFailures: newConsecutiveFailures
        }
      })

      // Trigger health check to confirm
      checkHealth()
    }

    window.addEventListener('service-status-error', handleServiceError as EventListener)
    return () => window.removeEventListener('service-status-error', handleServiceError as EventListener)
  }, [checkHealth])

  // Dismiss banner for this session/incident
  const dismissBanner = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, new Date().toISOString())
    } catch {
      // sessionStorage not available
    }
    setState(prev => ({ ...prev, userDismissed: true }))
  }, [])

  // Collapse banner (show minimal state)
  const collapseBanner = useCallback(() => {
    try {
      sessionStorage.setItem(COLLAPSED_KEY, 'true')
    } catch {
      // sessionStorage not available
    }
    setState(prev => ({ ...prev, isCollapsed: true }))
  }, [])

  // Expand banner (show full details)
  const expandBanner = useCallback(() => {
    try {
      sessionStorage.setItem(COLLAPSED_KEY, 'false')
    } catch {
      // sessionStorage not available
    }
    setState(prev => ({ ...prev, isCollapsed: false }))
  }, [])

  // Report an error from passive detection (e.g., axios interceptor)
  const reportError = useCallback((service: 'database' | 'ai', error: Error) => {
    setState(prev => {
      const newConsecutiveFailures = prev.consecutiveFailures + 1

      // Only update state if we haven't already detected issues
      if (prev.services[service].status !== 'healthy') {
        return prev
      }

      // Require multiple failures before showing as degraded
      if (newConsecutiveFailures < MIN_FAILURES_TO_SHOW) {
        return { ...prev, consecutiveFailures: newConsecutiveFailures }
      }

      return {
        ...prev,
        overall: 'degraded',
        services: {
          ...prev.services,
          [service]: {
            ...prev.services[service],
            status: 'degraded',
            message: error.message
          }
        },
        detectedAt: prev.detectedAt || new Date(),
        consecutiveFailures: newConsecutiveFailures
      }
    })

    // Trigger immediate health check to confirm
    checkHealth()
  }, [checkHealth])

  const contextValue: ServiceStatusContextType = {
    ...state,
    dismissBanner,
    collapseBanner,
    expandBanner,
    reportError
  }

  return (
    <ServiceStatusContext.Provider value={contextValue}>
      {children}
    </ServiceStatusContext.Provider>
  )
}

/**
 * Hook to access service status state and actions.
 * Use this to check service health and show status banners.
 */
export function useServiceStatus() {
  const context = useContext(ServiceStatusContext)
  if (context === undefined) {
    throw new Error('useServiceStatus must be used within a ServiceStatusProvider')
  }
  return context
}

/**
 * Hook to get the appropriate message for the current service status.
 */
export function useServiceStatusMessage() {
  const { overall, services } = useServiceStatus()

  if (overall === 'healthy') {
    return null
  }

  // Determine which service is affected
  const dbUnhealthy = services.database.status !== 'healthy'
  const aiUnhealthy = services.ai.status !== 'healthy'

  // Multiple services affected
  if (dbUnhealthy && aiUnhealthy) {
    return SERVICE_MESSAGES.multiple[overall]
  }

  // Single service affected
  if (dbUnhealthy) {
    return SERVICE_MESSAGES.database[services.database.status]
  }

  if (aiUnhealthy) {
    return SERVICE_MESSAGES.ai[services.ai.status]
  }

  // Fallback
  return SERVICE_MESSAGES.multiple[overall]
}
